// The sync protocol (RK-SYS-006 section 3.1): upload photos and voice notes, send the outbox in
import { isOnline } from "./useOnline";
// recorded order, store each response, replace temporary IDs, refresh the cache, record last sync.
import { db, getMeta, setMeta, type FileBlob, type OutboxOp } from "./db";
import { refreshCache } from "./cache";
import { currentSession } from "../lib/supabase";
import { callFunction, performOnline, ApiError } from "../lib/api";
import { recordClientError } from "../lib/errors";
import { env } from "../lib/env";
import { newId } from "../lib/ids";
import { summarize, type SelectionSummary } from "./picks";
import { fileBytesOf, fileSize, notKeptReason } from "./outbox";
import { fileRefs, isWaiting } from "./waiting";

export type SyncOutcome = { sent: number; applied: number; rejected: number; held: number; proposed?: number; ids: { temp_id: string; register_id: string }[]; error?: string | undefined;
  /** Decision 84 #1: what became of the files tried this round, and each selection they belong to, counted whole. */
  files?: { sent: number; duplicates: number; waiting: number; failed: number }; selections?: SelectionSummary[] };

let running: Promise<SyncOutcome> | null = null;
let runningSince = 0;
let lastOfflineAt = 0;
if (typeof window !== "undefined") window.addEventListener("offline", () => { lastOfflineAt = Date.now(); });

export async function deviceId(): Promise<string> {
  let id = await getMeta<string | null>("device_id", null);
  if (!id) {
    id = newId();
    await setMeta("device_id", id);
  }
  return id;
}

export { isWaiting } from "./waiting";

/** Replaces "file:<id>" references in a payload with the uploaded document's ID. */
function replaceFiles(value: unknown, map: Map<string, string>): unknown {
  if (typeof value === "string" && value.startsWith("file:")) return map.get(value.slice(5)) ?? null;
  if (Array.isArray(value)) return value.map((v) => replaceFiles(v, map));
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, replaceFiles(v, map)]));
  return value;
}


/** A change that needs a file still on the phone is held back to a later round; it is never sent with its file dropped. */
async function needsWaitingFile(op: OutboxOp, map: Map<string, string>): Promise<boolean> {
  for (const id of fileRefs(op.payload)) {
    if (map.has(id)) continue;
    const f = await db.files.get(id);
    if (f && !f.uploaded) return true;
  }
  return false;
}

/**
 * Decision 84 #1: the server is told what was picked before any file is sent (document.intend), so a file that never
 * arrives is still accounted for there. Every pick and every waiting file not yet announced, by picked set.
 */
async function announce(): Promise<void> {
  const picks = await db.picks.filter((p) => !p.announced).toArray();
  const pickIds = new Set(await db.picks.toCollection().primaryKeys());
  const files = await db.files.filter((f) => isWaiting(f) && !f.announced && !pickIds.has(f.id)).toArray();
  const entries = [
    ...picks.map((p) => ({ table: "picks" as const, id: p.id, batch: p.upload_batch ?? null, file: { client_file_id: p.id, name: p.name, size: p.size, type: p.type } })),
    ...files.map((f) => ({ table: "files" as const, id: f.id, batch: f.upload_batch ?? null, file: { client_file_id: f.id, name: f.filename, size: fileSize(f), type: f.mime } }))
  ];
  const batches = new Map<string | null, typeof entries>();
  for (const e of entries) batches.set(e.batch, [...(batches.get(e.batch) ?? []), e]);
  for (const [batch, list] of batches) {
    for (let i = 0; i < list.length; i += 500) {
      const part = list.slice(i, i + 500);
      const r = await performOnline("document.intend", { upload_batch: batch, files: part.map((e) => e.file) });
      if (!r.ok) {
        recordClientError(`The picked files could not be announced: ${r.message ?? "no answer"}`, { action: "document.intend", files: part.length, code: r.code });
        continue;
      }
      await db.transaction("rw", db.picks, db.files, async () => {
        for (const e of part) await (e.table === "picks" ? db.picks.update(e.id, { announced: true }) : db.files.update(e.id, { announced: true }));
      });
    }
  }
}

/** Tells the server what was just picked, at once (the uploads follow at sync); a failure is retried at the next sync. */
export async function announcePicked(): Promise<void> {
  if (!isOnline() || !(await currentSession())) return;
  await announce().catch((e) => recordClientError(`The picked files could not be announced: ${e instanceof Error ? e.message : String(e)}`, { action: "document.intend" }));
}

type UploadAnswer = { ok?: boolean; document_id?: string; duplicate_arrival?: boolean; message?: string; code?: string };
type Tally = NonNullable<SyncOutcome["files"]>;

/**
 * Uploads one file. Decision 84 #1: no connection or a server error (5xx, 408) leaves it waiting for the next round and
 * the round moves on; a refusal (another 4xx) marks it failed with the server's message, word for word, and it is not
 * sent again until the person retries it. Every failure is logged with the file's name, size, type and the status. The
 * file's document is saved to file-docs at once. Round 2 (F2): an expired sign-in (401) or a rate limit (429) ends the
 * round ("stop"): this file and the rest stay waiting, never failed, for a later round, so a batch is not sent into a
 * limit that refuses them all. The file's bytes are read here only, once per upload (F4 a).
 */
async function uploadOne(f: FileBlob, map: Map<string, string>, tally: Tally): Promise<"stop" | void> {
  const bytes = await fileBytesOf(f);
  if (!bytes) {
    // Decision 85 #2: a record whose bytes are not on the phone is never skipped in silence: it is named, to pick again.
    const reason = notKeptReason(f.filename);
    recordClientError(`A file waiting on this phone has no bytes kept: ${reason}`, { filename: f.filename, size: fileSize(f), mime: f.mime, client_file_id: f.id });
    await db.files.update(f.id, { failed_reason: reason });
    tally.failed++;
    return;
  }
  const form = new FormData();
  form.set("file", new File([bytes], f.filename, { type: f.mime }));
  form.set("source", f.source);
  form.set("operation_id", f.operation_id);
  form.set("from_sync", "1");
  form.set("client_file_id", f.id);
  if (f.doc_type) form.set("doc_type", f.doc_type);
  if (f.transcript) form.set("transcript", f.transcript);
  if (f.upload_batch) form.set("upload_batch", f.upload_batch);
  let status = 0;
  let r: UploadAnswer | null = null;
  let reason = "";
  try {
    r = await callFunction<UploadAnswer>("documents/upload", undefined, { form, onStatus: (s) => { status = s; } });
  } catch (e) {
    status = e instanceof ApiError ? e.status : status;
    reason = e instanceof Error ? e.message : String(e);
  }
  const answer = r;
  if (answer?.ok && answer.document_id) {
    map.set(f.id, answer.document_id);
    // The bytes are on the server now: the phone keeps only the record, so its store does not fill up.
    await db.transaction("rw", db.files, db.fileBytes, db.meta, async () => {
      await db.files.update(f.id, { uploaded: true, duplicate: answer.duplicate_arrival === true, failed_reason: undefined, ...(f.blob !== undefined ? { blob: undefined } : {}) });
      await db.fileBytes.delete(f.id);
      await setMeta("file-docs", Object.fromEntries(map));
    });
    if (answer.duplicate_arrival) tally.duplicates++; else tally.sent++;
    return;
  }
  // A refusal by the network before the function (no message of its own) is still named plainly.
  reason = answer?.message || (status === 413 ? "The file is larger than 50 MB. Send a smaller copy." : "") || reason || "The server did not confirm the file.";
  const stop = status === 401 || status === 429;
  const refused = status >= 400 && status < 500 && !stop && status !== 408;
  recordClientError(`A file could not be uploaded (${status ? `status ${status}` : "no connection"}): ${reason}`,
    { filename: f.filename, size: fileSize(f), mime: f.mime, status, reason, client_file_id: f.id, kept_waiting: !refused, ...(stop ? { round_stopped: true } : {}) });
  if (refused) {
    await db.files.update(f.id, { failed_reason: reason });
    tally.failed++;
  } else tally.waiting++;
  return stop ? "stop" : undefined;
}

/** Only one upload loop runs at a time (Decision 84 #1): a round started meanwhile sends the outbox without uploading. */
let uploadLoop: Promise<string[]> | null = null;

/**
 * Uploads every waiting file, each on its own; returns the ids tried. Files kept while the loop runs are taken too. A 401
 * or 429 ends the loop (round 2, F2): the files not tried stay waiting and are counted as waiting in their sets.
 */
async function uploadFiles(map: Map<string, string>, tally: Tally): Promise<string[]> {
  const tried = new Set<string>();
  try {
    await announce();
  } catch (e) {
    recordClientError(`The picked files could not be announced: ${e instanceof Error ? e.message : String(e)}`, { action: "document.intend" });
  }
  let stopped = false;
  while (!stopped) {
    const next = (await db.files.filter((x) => isWaiting(x) && !tried.has(x.id)).primaryKeys()) as string[];
    if (!next.length) break;
    // In the order picked (files kept before picks were recorded, or not from the Inbox, first).
    const picks = await db.picks.bulkGet(next);
    const rank = new Map(next.map((id, i) => [id, picks[i] ? `${picks[i]!.picked_at}|${String(picks[i]!.order ?? 0).padStart(6, "0")}` : ""]));
    next.sort((a, b) => rank.get(a)!.localeCompare(rank.get(b)!));
    for (const id of next) {
      tried.add(id);
      const f = await db.files.get(id);
      if (!f || !isWaiting(f)) continue; // removed, retried or sent meanwhile
      if ((await uploadOne(f, map, tally)) === "stop") { stopped = true; break; }
    }
    if (!isOnline()) break;
  }
  return [...tried];
}

type TempId = { temp_id?: string | null; register_id?: string | null };
type SyncResult = { operation_id: string; ok: boolean; duplicate?: boolean; proposed?: boolean; status?: string; code?: string; message?: string; items?: (TempId & { animals?: TempId[] })[] };

/** A group split into animals in a camp returns each animal's ID inside its item (BD-106). */
function rememberIds(r: SyncResult, temp: Record<string, string>, outcome: SyncOutcome): void {
  for (const it of (r.items ?? []).flatMap((x) => [x, ...(x.animals ?? [])])) if (it.temp_id && it.register_id) { temp[it.temp_id] = it.register_id; outcome.ids.push({ temp_id: it.temp_id, register_id: it.register_id }); }
}

async function applyResults(ops: OutboxOp[], results: SyncResult[], outcome: SyncOutcome) {
  const temp = await getMeta<Record<string, string>>("temp-ids", {});
  for (const r of results) {
    const op = ops.find((o) => o.operation_id === r.operation_id);
    if (!op) continue;
    if (r.status === "held") {
      await db.outbox.update(op.operation_id, { status: "held", message: r.message });
      outcome.held++;
    } else if (r.ok || r.duplicate) {
      rememberIds(r, temp, outcome);
      await db.outbox.delete(op.operation_id);
      outcome.applied++;
      // A farm staff report is a proposal for a GM (decision 17): nothing changes until it is approved.
      if (r.proposed) outcome.proposed = (outcome.proposed ?? 0) + 1;
    } else {
      await db.outbox.update(op.operation_id, { status: "rejected", message: r.message, attempts: op.attempts + 1 });
      outcome.rejected++;
    }
  }
  await setMeta("temp-ids", temp);
}

/** One round of file uploads, unless another round is already under way (then nothing is tried here). */
async function uploadRound(files: Map<string, string>, tally: Tally): Promise<string[]> {
  if (uploadLoop) return [];
  const loop = uploadFiles(files, tally);
  uploadLoop = loop;
  try { return await loop; } finally { if (uploadLoop === loop) uploadLoop = null; }
}

/** What happened to each selection a file of which was tried this round. */
async function summariseSelections(tried: string[]) {
  const selections = new Set((await db.picks.bulkGet(tried)).flatMap((p) => (p ? [p.selection] : [])));
  return Promise.all([...selections].map(summarize));
}

async function sendOps(): Promise<SyncOutcome> {
  const outcome: SyncOutcome = { sent: 0, applied: 0, rejected: 0, held: 0, ids: [] };
  if (!isOnline()) return { ...outcome, error: "offline" };
  const session = await currentSession();
  const ops = await db.outbox.where("status").anyOf("waiting", "sending").sortBy("recorded_at");
  if (!session) {
    // Decision 84 #1: files waiting on the phone need a sign-in too, even with nothing in the outbox.
    if (ops.length || (await db.files.filter(isWaiting).count())) window.dispatchEvent(new CustomEvent("rk:sync-needs-signin"));
    return { ...outcome, error: "signin" };
  }
  const files = new Map<string, string>(Object.entries(await getMeta<Record<string, string>>("file-docs", {})));
  const tally: Tally = { sent: 0, duplicates: 0, waiting: 0, failed: 0 };
  const tried = await uploadRound(files, tally);
  if (tried.length) {
    outcome.files = tally;
    outcome.selections = await summariseSelections(tried);
  }
  const device = await deviceId();
  // A change that needs a file still on the phone waits for a later round (it stays in the outbox).
  const ready: OutboxOp[] = [];
  for (const o of ops) if (!(await needsWaitingFile(o, files))) ready.push(o);
  if (ready.length) {
    const prepared = ready.map((o) => ({ operation_id: o.operation_id, action: o.action, payload: replaceFiles(o.payload, files), recorded_at: o.recorded_at }));
    const r = await callFunction<{ ok: boolean; results: SyncResult[] }>("sync", { device_id: device, app_version: env.appVersion, operations: prepared });
    outcome.sent = ready.length;
    await applyResults(ready, r.results ?? [], outcome);
  } else {
    await callFunction("sync", { device_id: device, app_version: env.appVersion, operations: [] });
  }
  await refreshCache();
  await setMeta("last_sync_at", new Date().toISOString());
  return outcome;
}

let again = false;

export function syncNow(): Promise<SyncOutcome> {
  // A sync that started long ago is not waited for: start afresh (requests time out on their own).
  if (running && (Date.now() - runningSince > 60_000 || runningSince < lastOfflineAt)) running = null;
  // Changes saved while a sync is under way are sent by a second round straight after it.
  if (running) { again = true; return running; }
  runningSince = Date.now();
  const current: Promise<SyncOutcome> = sendOps()
    .catch((e) => {
      console.warn(`Sync did not finish: ${e instanceof Error ? e.message : String(e)}`);
      return { sent: 0, applied: 0, rejected: 0, held: 0, ids: [], error: e instanceof ApiError ? e.message : String(e) };
    })
    .finally(() => { if (running === current) running = null; })
    .then((o) => {
      window.dispatchEvent(new CustomEvent("rk:synced", { detail: o }));
      if (again) { again = false; if (isOnline()) void syncNow(); }
      return o;
    });
  running = current;
  return running;
}

/** Sends the outbox of a user whose login was switched off, proven by Face ID or fingerprint; the server holds it for the CEO (REQ-OFF-08). */
export async function sendHeldWithPasskey(assertion: { challenge_id: string; response: unknown }): Promise<SyncOutcome> {
  const outcome: SyncOutcome = { sent: 0, applied: 0, rejected: 0, held: 0, ids: [] };
  const ops = await db.outbox.where("status").anyOf("waiting", "sending").sortBy("recorded_at");
  if (!ops.length) return outcome;
  const r = await callFunction<{ ok: boolean; results: SyncResult[]; message?: string }>("sync/held", {
    ...assertion, device_id: await deviceId(), operations: ops.map((o) => ({ operation_id: o.operation_id, action: o.action, payload: o.payload, recorded_at: o.recorded_at }))
  }, { auth: false });
  if (!r.ok) return { ...outcome, error: r.message };
  outcome.sent = ops.length;
  await applyResults(ops, r.results ?? [], outcome);
  return outcome;
}

export function startAutoSync(): () => void {
  const kick = () => { if (isOnline()) void syncNow(); };
  window.addEventListener("online", kick);
  window.addEventListener("rk:outbox-changed", kick);
  const t = window.setInterval(kick, 60_000);
  document.addEventListener("visibilitychange", kick);
  kick();
  return () => {
    window.removeEventListener("online", kick);
    window.removeEventListener("rk:outbox-changed", kick);
    document.removeEventListener("visibilitychange", kick);
    window.clearInterval(t);
  };
}
